import { EventType } from "@/analytics/analytics-events";
import { ImportScansProperties } from "@/analytics/import-scans-properties";
import { useDataManagementDroneHandler } from "@/hooks/use-data-management-drone-handler";
import { useAppSelector } from "@/store/store-hooks";
import { selectCurrentUser } from "@/store/user-selectors";
import { DraftViewOpenFrom } from "@/utils/redirects";
import { DataManagementPicker } from "@faro-lotv/data-management";
import {
  DroneIcon,
  FaroText,
  FolderSmallIcon,
  grey,
  NO_TRANSLATE_CLASS,
} from "@faro-lotv/flat-ui";
import { Analytics } from "@faro-lotv/foreign-observers";
import { selectCompanyId, selectProjectId } from "@faro-lotv/project-source";
import { Stack } from "@mui/material";
import type { JSX } from "react";
import { ImportCard } from "./import-card";

interface Props {
  /** Identifier for the calling UI page/element provided to navigateToDataManagement. */
  from: DraftViewOpenFrom;
  /** Optional callback for closing the surrounding import data dialog. */
  onClose?(): void;
}

/**
 * @returns Card for importing drone data (photogrammetry photos) via the Data Management editor.
 * Used in:
 *  - Import data dialog opened with the "Import" button.
 *  - Start screen shown for an empty project and a user with write access.
 */
export function ImportDroneDataCard({
  from,
  onClose,
}: Props): JSX.Element | null {
  const currentUser = useAppSelector(selectCurrentUser);
  const companyId = useAppSelector(selectCompanyId);
  const projectId = useAppSelector(selectProjectId);

  const {
    shouldOpenFileExplorer,
    setShouldOpenFileExplorer,
    isCheckingFiles,
    setIsCheckingFiles,
    onFinishedCheckingFiles,
  } = useDataManagementDroneHandler({ projectId, from, onClose });

  if (!currentUser || !companyId || !projectId) {
    return null;
  }

  return (
    <>
      <DataManagementPicker
        type="photogrammetry"
        currentUser={currentUser}
        companyId={companyId}
        projectId={projectId}
        shouldOpenFileExplorer={shouldOpenFileExplorer}
        setShouldOpenFileExplorer={setShouldOpenFileExplorer}
        isChecking={isCheckingFiles}
        setIsChecking={setIsCheckingFiles}
        onFinished={onFinishedCheckingFiles}
      />

      <ImportCard
        ariaLabel="import drone data"
        icon={<DroneIcon sx={{ width: "46px", height: "46px" }} />}
        title="Drone data"
        hasBetaBadge
        description="Import photos captured by drones. The data will be processed to generate a point cloud that represents the surface shape of your site."
        footer={
          <Stack
            direction="row"
            sx={{ justifyContent: "space-between", alignItems: "center" }}
          >
            <FaroText
              aria-label="drone data extensions"
              variant="bodyS"
              className={NO_TRANSLATE_CLASS}
              gap={1}
            >
              .jpg with geotag
              <br />
              Minimum 6 photos required
            </FaroText>
            <FolderSmallIcon sx={{ color: grey[600], ml: 1 }} />
          </Stack>
        }
        onClick={() => {
          Analytics.track<ImportScansProperties>(EventType.importDroneData, {
            via: from === "draftImportScansDialog" ? "draft view" : "live view",
          });
          setShouldOpenFileExplorer(true);
        }}
      />
    </>
  );
}
